import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Download, Eye } from "lucide-react";
import { BillPreviewModal } from "./Bills";

export default function Reports() {
  const [rows, setRows] = useState([]);
  const [claims, setClaims] = useState([]);
  const [bills, setBills] = useState([]);
  const [tab, setTab] = useState("customers");
  const [preview, setPreview] = useState(null);

  const loadBills = () => api.get("/bills").then(r => setBills(r.data));

  useEffect(() => {
    api.get("/reports/customers").then(r => setRows(r.data));
    api.get("/claims").then(r => setClaims(r.data));
    loadBills();
  }, []);

  const exportCsv = (data, name) => {
    if (!data.length) return;
    const keys = Object.keys(data[0]);
    const csv = [keys.join(","), ...data.map(d => keys.map(k => JSON.stringify(d[k] ?? "")).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${name}-${Date.now()}.csv`;
    a.click();
  };

  return (
    <Layout title="Reports" subtitle="Insights"
      action={
        <button className="btn-dark flex items-center gap-2" onClick={() => exportCsv(tab==="customers"?rows: tab==="claims"?claims:bills, tab)} data-testid="export-csv-btn">
          <Download size={14} /> Export CSV
        </button>
      }>
      <div className="flex gap-1 mb-5">
        {["customers","claims","bills"].map(t => (
          <button key={t} className={`px-5 py-2 text-[12px] uppercase tracking-wider font-semibold border ${tab===t?"bg-[#1A1A1A] text-white border-[#1A1A1A]":"border-[#E6E4E0] text-[#8C8C8C]"}`}
                  onClick={() => setTab(t)} data-testid={`tab-${t}`}>{t}</button>
        ))}
      </div>

      <div className="card-sharp">
        {tab === "customers" && (
          <table className="fin">
            <thead><tr><th>Name</th><th>Mobile</th><th>Registered Via</th><th>Tier</th><th>Points</th><th>Bills</th><th>Claims</th><th>Joined</th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td className="font-medium">{r.name}</td>
                  <td className="font-mono text-[12px]">{r.wa_number}</td>
                  <td className="text-[12px] text-[#595959]">
                    {r.registered_brand ? <span className="badge badge-gold mr-1">{r.registered_brand}</span> : null}
                    {r.registered_location ? <span className="badge badge-pending">{r.registered_location}</span> : null}
                    {!r.registered_brand && !r.registered_location && <span className="text-[#8C8C8C]">Direct</span>}
                  </td>
                  <td><span className="badge badge-gold">{r.tier || "Silver"}</span></td>
                  <td>{r.points || 0}</td>
                  <td>{r.bills_count}</td>
                  <td>{r.claims_count}</td>
                  <td className="text-[12px] text-[#8C8C8C]">{r.created_at?.slice(0,10)}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan="8" className="text-center py-8 text-[#8C8C8C]">No data.</td></tr>}
            </tbody>
          </table>
        )}
        {tab === "claims" && (
          <table className="fin">
            <thead><tr><th>Customer</th><th>Mobile</th><th>Brand</th><th>Offer</th><th>Claimed</th><th>Valid Until</th></tr></thead>
            <tbody>
              {claims.map(c => (
                <tr key={c.id}>
                  <td>{c.customer_name}</td>
                  <td className="font-mono text-[12px]">{c.customer_wa}</td>
                  <td>{c.brand_name}</td>
                  <td>{c.offer_title}</td>
                  <td className="text-[12px]">{c.claimed_at?.slice(0,16).replace("T"," ")}</td>
                  <td className="text-[12px]">{c.valid_until?.slice(0,10)}</td>
                </tr>
              ))}
              {claims.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-[#8C8C8C]">No claims.</td></tr>}
            </tbody>
          </table>
        )}
        {tab === "bills" && (
          <table className="fin">
            <thead><tr><th>Customer</th><th>Mobile</th><th>Type</th><th>Status / Reason</th><th>Uploaded</th><th></th></tr></thead>
            <tbody>
              {bills.map(b => (
                <tr key={b.id} data-testid={`rep-bill-row-${b.id}`}>
                  <td>{b.customer_name}</td>
                  <td className="font-mono text-[12px]">{b.customer_wa}</td>
                  <td>{b.media_type}</td>
                  <td>
                    <span className={`badge badge-${b.status}`}>{b.status}</span>
                    {b.status === "rejected" && b.verify_notes && (
                      <div className="text-[11px] text-[#8C3A3A] italic mt-1 max-w-xs">“{b.verify_notes}”</div>
                    )}
                  </td>
                  <td className="text-[12px]">{b.created_at?.slice(0,16).replace("T"," ")}</td>
                  <td className="text-right">
                    {b.file_path && (
                      <button className="btn-outline" style={{padding:"6px 10px"}} onClick={() => setPreview(b)} data-testid={`rep-preview-bill-${b.id}`}>
                        <Eye size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {bills.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-[#8C8C8C]">No bills.</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {preview && (
        <BillPreviewModal bill={preview} onClose={() => setPreview(null)} onChanged={() => { loadBills(); setPreview(null); }} />
      )}
    </Layout>
  );
}
